// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Agent accounts on a machine card in Settings → Connections → Agents (docs/konvo-agents/
// HEALTH.md, design/agents-health C-open-desk-m1, account-add-desk-g2, account-signin-desk-g2): the
// Claude and Codex sign-ins T3 Code uses on the machine (konvo-agents 0.51.0+, can.accounts), each
// with its status, Sign in, edit and a menu to turn it off or remove it, and Add account. The editor
// names an account, gives it a colour and a sign-in folder, and under Advanced a CLI path and
// environment variables (their values never come back from the machine: kept ones show by name).
// Sign in runs the account's login command in a Konvo terminal on the machine when terminals are on
// there, else shows the command to copy; Check again asks the machine (the setup op) afterwards.

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import styles from '@app/features/konvo_agents/components/AgentAccountsSettings.module.css';
import {CommandBlock} from '@app/features/konvo_agents/components/AgentBits';
import {ProviderLogo} from '@app/features/konvo_agents/components/AgentBrand';
import {connectionLabel} from '@app/features/konvo_agents/components/AgentConnectionCopy';
import {accountName, agentName} from '@app/features/konvo_agents/components/AgentHealthCopy';
import {agentProblemMessage} from '@app/features/konvo_agents/components/AgentsCopy';
import {AgentTerminalScreen} from '@app/features/konvo_agents/components/AgentTerminalScreen';
import panelStyles from '@app/features/konvo_agents/components/AgentTerminalsPanel.module.css';
import {accountLabel} from '@app/features/konvo_agents/state/AgentAccounts';
import {machineConnections} from '@app/features/konvo_agents/state/AgentConnections';
import {
	type AccountEnvRow,
	type AccountForm,
	accountRequest,
	defaultHomePath,
	ENV_NAME,
	formFromConnection,
} from '@app/features/konvo_agents/state/AgentHealth';
import Agents from '@app/features/konvo_agents/state/Agents';
import {runInTerminal} from '@app/features/konvo_agents/state/AgentTerminalRun';
import AgentTerminalSessions, {canUseTerminalSessions} from '@app/features/konvo_agents/state/AgentTerminalSessions';
import {Accordion} from '@app/features/ui/accordion/Accordion';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input} from '@app/features/ui/components/form/FormInput';
import {ActionButton, IconButton, Notice, Segmented, StatusBadge} from '@app/features/ui/konvo_controls/KonvoControls';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {choiceCardStyles} from '@app/features/user/components/modals/tabs/appearance_tab/cards/ChoiceCard';
import type {AgentAccountDriver, AgentConnection, AgentMachineView} from '@fluxer/schema/src/domains/konvo/Agents';
import {agentPlanShortName} from '@fluxer/schema/src/domains/konvo/AgentUsage';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	IconCheck,
	IconChevronRight,
	IconCircleCheck,
	IconDots,
	IconFolder,
	IconInfoCircle,
	IconKey,
	IconPencil,
	IconPlayerPause,
	IconPlayerPlay,
	IconPlus,
	IconRefresh,
	IconTrash,
	IconX,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

const EDIT_KEY = 'konvo-agents-account-edit';
const SIGN_IN_KEY = 'konvo-agents-account-sign-in';

/** The accent colours an account can take (picker letter badges and Agents' account pills). */
export const ACCOUNT_COLOURS = ['#18181b', '#ea580c', '#2563eb', '#16a34a', '#9333ea', '#db2777'] as const;
const COLOUR_NAMES = [
	msg({message: 'Black', comment: 'Agents account colour swatch.'}),
	msg({message: 'Orange', comment: 'Agents account colour swatch.'}),
	msg({message: 'Blue', comment: 'Agents account colour swatch.'}),
	msg({message: 'Green', comment: 'Agents account colour swatch.'}),
	msg({message: 'Purple', comment: 'Agents account colour swatch.'}),
	msg({message: 'Pink', comment: 'Agents account colour swatch.'}),
];

/** Whether Konvo can add and edit accounts on the machine (konvo-agents 0.51.0+). */
export function canManageAccounts(machine: AgentMachineView): boolean {
	return machine.tools.some((tool) => tool.can.accounts === true);
}

const driverOf = (connection: AgentConnection): AgentAccountDriver =>
	connection.driver === 'codex' ? 'codex' : 'claudeAgent';

/** The account's own name: "Nuvio" for "Claude · Nuvio". */
function shortName(connection: AgentConnection): string {
	return connection.config?.displayName ?? accountLabel(connection.providerName, connection.provider);
}

/** "~/.claude-personal", or the agent's default folder. */
function homeOf(connection: AgentConnection): string {
	return connection.config?.homePath || (driverOf(connection) === 'codex' ? '~/.codex' : '~/.claude');
}

/** The plan the account reports ("Pro", "Max"), when its usage is known. */
function planOf(machine: AgentMachineView, connection: AgentConnection): string | undefined {
	const plan = machine.tools
		.flatMap((tool) => tool.usage ?? [])
		.find((entry) => entry.provider === connection.provider);
	return plan ? agentPlanShortName(plan) : undefined;
}

const SIGNED_IN = msg({message: 'Signed in', comment: 'Agents accounts: the account is signed in.'});
const SIGNED_IN_PLAN = msg({
	message: 'Signed in · {plan}',
	comment: 'Agents accounts: the account is signed in on this plan, e.g. "Signed in · Pro".',
});
const TURNED_OFF = msg({message: 'Turned off', comment: 'Agents accounts: the account is turned off.'});

function AccountLogo({
	machine,
	connection,
	size = 22,
}: {
	machine: AgentMachineView;
	connection: AgentConnection;
	size?: number;
}) {
	const account = Agents.accountsOn(machine.id).get(connection.provider) ?? null;
	return (
		<span className={styles.logo}>
			<ProviderLogo provider={connection.driver} size={size} account={account} />
		</span>
	);
}

const AccountRow = observer(({machine, connection}: {machine: AgentMachineView; connection: AgentConnection}) => {
	const {i18n, t} = useLingui();
	const [busy, setBusy] = useState(false);
	const name = shortName(connection);
	const fullName = accountName(connection.providerName, connection.driver);
	const machineName = machine.name;
	const enabled = connection.config?.enabled ?? connection.status !== 'disabled';
	const plan = planOf(machine, connection);
	const status = connection.status;
	const save = async (patch: {enabled: boolean}) => {
		setBusy(true);
		try {
			await Agents.saveAccount(machine.id, {...accountRequest(formFromConnection(connection), connection), ...patch});
		} catch (error) {
			ToastCommands.error(agentProblemMessage(i18n, error));
		} finally {
			setBusy(false);
		}
	};
	const remove = () =>
		ModalCommands.push(
			ModalCommands.modal(() => (
				<ConfirmModal
					title={t({message: `Remove ${fullName}?`, comment: 'Agents accounts: confirm removing an account.'})}
					description={t({
						message: `T3 Code stops offering ${fullName} on ${machineName}. Its sign-in folder stays on the machine, so adding it again signs in at once.`,
						comment: 'Agents accounts: what removing an account does.',
					})}
					primaryText={t({message: 'Remove', comment: 'Agents accounts: confirm button that removes an account.'})}
					primaryVariant="danger"
					onPrimary={async () => {
						try {
							await Agents.saveAccount(machine.id, {action: 'remove', instanceId: connection.provider});
						} catch (error) {
							ToastCommands.error(agentProblemMessage(i18n, error));
						}
					}}
				/>
			)),
		);
	const openMenu = (event: React.MouseEvent<HTMLButtonElement>) =>
		ContextMenuCommands.openFromElementBottomRight(event, ({onClose}) => (
			<MenuGroup>
				<MenuItem
					icon={enabled ? <IconPlayerPause size={16} stroke={1.7} /> : <IconPlayerPlay size={16} stroke={1.7} />}
					onClick={() => {
						onClose();
						void save({enabled: !enabled});
					}}
				>
					{enabled
						? t({message: 'Turn off', comment: 'Agents accounts menu: stop T3 Code offering the account.'})
						: t({message: 'Turn on', comment: 'Agents accounts menu: offer the account again.'})}
				</MenuItem>
				<MenuItem
					danger
					icon={<IconTrash size={16} stroke={1.7} />}
					onClick={() => {
						onClose();
						remove();
					}}
				>
					{t({message: 'Remove', comment: 'Agents accounts menu: remove the account from T3 Code.'})}
				</MenuItem>
			</MenuGroup>
		));
	const statusText =
		status === 'ready'
			? plan
				? i18n._(SIGNED_IN_PLAN, {plan})
				: i18n._(SIGNED_IN)
			: status === 'disabled' || !enabled
				? i18n._(TURNED_OFF)
				: connectionLabel(i18n, status);
	const problem = status === 'sign_in' ? 'bad' : status === 'not_installed' || status === 'unavailable' ? 'warn' : null;
	return (
		<li
			className={clsx(styles.row, !enabled && styles.rowOff)}
			data-flx="konvo-agents.accounts.row"
			data-provider={connection.provider}
			data-status={status}
		>
			<AccountLogo machine={machine} connection={connection} />
			<span className={styles.rowText}>
				<span className={styles.rowName}>{name}</span>
				<span className={styles.rowSub}>
					{agentName(connection.driver)} · {homeOf(connection)}
				</span>
			</span>
			{problem ? (
				<StatusBadge state={problem} label={statusText} />
			) : (
				<span className={styles.rowStatus}>{statusText}</span>
			)}
			{status === 'sign_in' && machine.online && (connection.loginCommand || canManageAccounts(machine)) && (
				<ActionButton
					tone="danger"
					small
					onClick={() => openAccountSignIn(machine, connection)}
					data-flx="konvo-agents.accounts.row.sign-in"
				>
					<Trans comment="Agents accounts: signs the account in.">Sign in</Trans>
				</ActionButton>
			)}
			<IconButton
				label={t({message: `Edit ${fullName}`, comment: 'Agents accounts: edit button on an account row.'})}
				icon={<IconPencil size={16} stroke={1.7} />}
				className={styles.rowButton}
				disabled={busy || !machine.online}
				onClick={() => openAccountEditor(machine, connection)}
				data-flx="konvo-agents.accounts.row.edit"
			/>
			<IconButton
				label={t({message: `More for ${fullName}`, comment: 'Agents accounts: menu button on an account row.'})}
				icon={<IconDots size={16} stroke={1.7} />}
				className={styles.rowButton}
				disabled={busy || !machine.online}
				onClick={openMenu}
				data-flx="konvo-agents.accounts.row.menu"
			/>
		</li>
	);
});

/** The machine card's Agent accounts: one row per Claude or Codex account, and Add account. */
export const MachineAccountsSection = observer(({machine}: {machine: AgentMachineView}) => {
	const {t} = useLingui();
	if (!canManageAccounts(machine)) return null;
	const connections = machineConnections(machine);
	const machineName = machine.name;
	const tool = machine.tools[0]?.name ?? 'T3 Code';
	const phone = MobileLayout.enabled;
	return (
		<div className={styles.section} data-flx="konvo-agents.accounts">
			<Accordion
				id={`agents-accounts-${machine.id}`}
				compact
				className={styles.accordion}
				title={
					<span className={styles.headline}>
						<IconKey size={18} stroke={1.7} className={styles.headIcon} />
						<span className={styles.sectionName}>
							<Trans comment="Agents Settings: heading of a machine's Claude and Codex accounts.">Agent accounts</Trans>
						</span>
						{!phone && (
							<span className={styles.lead}>
								<Trans comment="Agents Settings: after 'Agent accounts'; {tool} is e.g. T3 Code, {machineName} the computer.">
									The Claude and Codex sign-ins {tool} uses on {machineName}
								</Trans>
							</span>
						)}
					</span>
				}
				headerAction={
					machine.online ? (
						<ActionButton
							small
							icon={<IconPlus size={15} stroke={2} />}
							onClick={() => openAccountEditor(machine, null)}
							data-flx="konvo-agents.accounts.add"
						>
							{phone ? (
								<Trans comment="Agents Settings on phones: adds a Claude or Codex account (short).">Add</Trans>
							) : (
								<Trans comment="Agents Settings: adds a Claude or Codex account on the machine.">Add account</Trans>
							)}
						</ActionButton>
					) : undefined
				}
			>
				<div className={styles.open}>
					{connections.length ? (
						<ul className={styles.rows}>
							{connections.map((connection) => (
								<AccountRow key={connection.provider} machine={machine} connection={connection} />
							))}
						</ul>
					) : (
						<p className={styles.quiet}>
							<Trans comment="Agents Settings: the machine reports no Claude or Codex accounts.">
								No accounts yet.
							</Trans>
						</p>
					)}
					<p className={styles.note}>
						<IconInfoCircle size={16} stroke={1.7} />
						{t({
							message: `Saved to ${tool} on ${machineName}. New chats can use an account at once; running chats keep theirs.`,
							comment: 'Agents Settings: under the accounts list. {tool} is e.g. T3 Code, {machineName} the computer.',
						})}
					</p>
				</div>
			</Accordion>
		</div>
	);
});

// ── Add / edit ──────────────────────────────────────────────────────────

function newForm(driver: AgentAccountDriver): AccountForm {
	return {driver, name: '', color: null, homePath: '', binaryPath: '', env: []};
}

const AccountEditor = observer(
	({machineId, provider, onClose}: {machineId: string; provider: string | null; onClose: () => void}) => {
		const {i18n, t} = useLingui();
		const machine = Agents.machine(machineId);
		const existing = provider
			? (machineConnections(machine).find((entry) => entry.provider === provider) ?? null)
			: null;
		const [form, setForm] = useState<AccountForm>(() =>
			existing ? formFromConnection(existing) : newForm('claudeAgent'),
		);
		// A new account's folder follows its name until someone types one.
		const [homeTouched, setHomeTouched] = useState(Boolean(existing));
		const [advanced, setAdvanced] = useState(
			Boolean(existing?.config?.binaryPath || existing?.config?.environmentNames.length),
		);
		const [saving, setSaving] = useState(false);
		const [error, setError] = useState<string | null>(null);
		const nameRef = useRef<HTMLInputElement>(null);
		if (!machine) return null;
		const machineName = machine.name;
		const tool = machine.tools[0]?.name ?? 'T3 Code';
		const set = (patch: Partial<AccountForm>) => setForm((current) => ({...current, ...patch}));
		const home = homeTouched ? form.homePath : form.name.trim() ? defaultHomePath(form.driver, form.name) : '';
		const brandName = form.driver === 'codex' ? 'Codex' : 'Claude';
		const trimmed = form.name.trim();
		const letter = (Array.from(trimmed)[0] ?? '').toUpperCase();
		const badEnv = form.env.some(
			(row) => !row.removed && !row.kept && row.name.trim() !== '' && !ENV_NAME.test(row.name.trim()),
		);
		const setEnv = (index: number, patch: Partial<AccountEnvRow>) =>
			set({env: form.env.map((row, at) => (at === index ? {...row, ...patch} : row))});
		const save = async () => {
			if (!trimmed || badEnv) return;
			setSaving(true);
			setError(null);
			const before = new Set(machineConnections(machine).map((entry) => entry.provider));
			try {
				const info = await Agents.saveAccount(machine.id, accountRequest({...form, homePath: home}, existing));
				onClose();
				if (!existing) {
					const added =
						info.connections?.find((entry) => !before.has(entry.provider) && shortName(entry) === trimmed) ??
						info.connections?.find((entry) => !before.has(entry.provider));
					if (added) openAccountSignIn(machine, added, {added: true});
				}
			} catch (cause) {
				setError(agentProblemMessage(i18n, cause));
			} finally {
				setSaving(false);
			}
		};
		return (
			<Modal.Root
				size="small"
				centered
				initialFocusRef={nameRef}
				onClose={onClose}
				data-flx="konvo-agents.accounts.editor"
			>
				<Modal.Header
					title={
						existing
							? t({
									message: `Edit ${accountName(existing.providerName, existing.driver)}`,
									comment: 'Agents accounts: title of the dialog that edits an account.',
								})
							: t({
									message: `Add an account on ${machineName}`,
									comment: 'Agents accounts: title of the dialog that adds an account; {machineName} is the computer.',
								})
					}
					onClose={onClose}
				/>
				<Modal.Content>
					<form
						className={styles.form}
						onSubmit={(event) => {
							event.preventDefault();
							void save();
						}}
					>
						{!existing && (
							<p className={styles.formLead}>
								<Trans comment="Agents accounts: under the Add account title. {tool} is e.g. T3 Code.">
									Another subscription for an agent, with its own sign-in. {tool} lists it next to the others.
								</Trans>
							</p>
						)}
						{!existing && (
							<div className={styles.field}>
								<span className={styles.fieldLabel}>
									<Trans comment="Agents accounts form: which agent the account is for.">Agent</Trans>
								</span>
								<Segmented
									value={form.driver}
									onChange={(driver) => set({driver})}
									options={[
										{
											value: 'claudeAgent',
											icon: <ProviderLogo provider="claudeAgent" size={16} />,
											children: 'Claude Code',
											'data-flx': 'konvo-agents.accounts.editor.driver.claude',
										},
										{
											value: 'codex',
											icon: <ProviderLogo provider="codex" size={16} />,
											children: 'Codex',
											'data-flx': 'konvo-agents.accounts.editor.driver.codex',
										},
									]}
									data-flx="konvo-agents.accounts.editor.driver"
								/>
							</div>
						)}
						<div className={styles.field}>
							<Input
								ref={nameRef}
								label={t({message: 'Name', comment: 'Agents accounts form: the account name.'})}
								value={form.name}
								maxLength={40}
								placeholder={t({message: 'Work 2', comment: 'Agents accounts form: example account name.'})}
								onChange={(event) => set({name: event.target.value})}
								footer={
									trimmed ? (
										<span className={styles.hint}>
											{t({
												message: `Pickers show it as ${brandName} · ${trimmed}, with a ${letter} badge.`,
												comment:
													'Agents accounts form: how the name shows; {brandName} is Claude or Codex, {letter} its first letter.',
											})}
										</span>
									) : undefined
								}
								data-flx="konvo-agents.accounts.editor.name"
							/>
						</div>
						<div className={styles.field}>
							<span className={styles.fieldLabel}>
								<Trans comment="Agents accounts form: the account's colour.">Colour</Trans>
							</span>
							<div
								className={clsx(choiceCardStyles.swatches, styles.swatches)}
								role="radiogroup"
								aria-label={t({message: 'Colour', comment: 'Agents accounts form: the account colour choices.'})}
							>
								{ACCOUNT_COLOURS.map((colour, index) => {
									const checked = form.color?.toLowerCase() === colour;
									return (
										<button
											key={colour}
											type="button"
											role="radio"
											aria-checked={checked}
											aria-label={i18n._(COLOUR_NAMES[index])}
											title={i18n._(COLOUR_NAMES[index])}
											className={clsx(choiceCardStyles.swatch, styles.swatch)}
											style={{background: colour}}
											onClick={() => set({color: checked ? null : colour})}
											data-flx="konvo-agents.accounts.editor.colour"
										>
											{checked && <IconCheck size={14} stroke={3} />}
										</button>
									);
								})}
							</div>
						</div>
						<div className={styles.field}>
							<Input
								label={t({
									message: 'Sign-in folder',
									comment: 'Agents accounts form: where the account keeps its sign-in.',
								})}
								value={home}
								leftIcon={<IconFolder size={16} stroke={1.7} />}
								placeholder={form.driver === 'codex' ? '~/.codex' : '~/.claude'}
								onChange={(event) => {
									setHomeTouched(true);
									set({homePath: event.target.value});
								}}
								footer={
									<span className={styles.hint}>
										<Trans comment="Agents accounts form: under the sign-in folder.">
											Each account keeps its sign-in in its own folder. Konvo makes it; leave it as it is unless you
											already have one.
										</Trans>
									</span>
								}
								data-flx="konvo-agents.accounts.editor.home"
							/>
						</div>
						<button
							type="button"
							className={styles.disclosure}
							aria-expanded={advanced}
							onClick={() => setAdvanced(!advanced)}
							data-flx="konvo-agents.accounts.editor.advanced"
						>
							<IconChevronRight
								size={16}
								stroke={1.8}
								className={clsx(styles.disclosureIcon, advanced && styles.open90)}
							/>
							<Trans comment="Agents accounts form: opens the CLI path and environment variables.">
								Advanced: CLI path, environment variables
							</Trans>
						</button>
						{advanced && (
							<div className={styles.advanced}>
								<Input
									label={t({
										message: 'CLI path',
										comment: 'Agents accounts form: the claude or codex executable to use.',
									})}
									value={form.binaryPath}
									placeholder={t({
										message: `The one ${tool} finds`,
										comment: 'Agents accounts form: placeholder of the CLI path; {tool} is e.g. T3 Code.',
									})}
									onChange={(event) => set({binaryPath: event.target.value})}
									data-flx="konvo-agents.accounts.editor.binary"
								/>
								<div className={styles.field}>
									<span className={styles.fieldLabel}>
										<Trans comment="Agents accounts form: heading over the environment variables.">
											Environment variables
										</Trans>
									</span>
									{form.env.map((row, index) =>
										row.removed ? null : (
											<div key={index} className={styles.envRow} data-flx="konvo-agents.accounts.editor.env">
												{row.kept ? (
													<span className={styles.envName}>{row.name}</span>
												) : (
													<input
														className={styles.envInput}
														value={row.name}
														placeholder="NAME"
														aria-label={t({message: 'Variable name', comment: 'Agents accounts form.'})}
														aria-invalid={row.name.trim() !== '' && !ENV_NAME.test(row.name.trim())}
														onChange={(event) => setEnv(index, {name: event.target.value})}
														data-flx="konvo-agents.accounts.editor.env.name"
													/>
												)}
												{row.kept && row.value === null ? (
													<span className={styles.envKept}>
														<Trans comment="Agents accounts form: an environment variable whose value stays on the machine (not shown).">
															value kept
														</Trans>
														<button
															type="button"
															className={styles.envLink}
															onClick={() => setEnv(index, {value: ''})}
															data-flx="konvo-agents.accounts.editor.env.replace"
														>
															<Trans comment="Agents accounts form: type a new value for a kept variable.">
																Replace
															</Trans>
														</button>
													</span>
												) : (
													<input
														className={styles.envInput}
														value={row.value ?? ''}
														placeholder={t({
															message: 'value',
															comment: 'Agents accounts form: placeholder of a variable value.',
														})}
														aria-label={t({message: 'Variable value', comment: 'Agents accounts form.'})}
														onChange={(event) => setEnv(index, {value: event.target.value})}
														data-flx="konvo-agents.accounts.editor.env.value"
													/>
												)}
												<IconButton
													label={t({message: 'Remove variable', comment: 'Agents accounts form.'})}
													icon={<IconX size={15} stroke={1.8} />}
													className={styles.rowButton}
													onClick={() =>
														row.kept
															? setEnv(index, {removed: true})
															: set({env: form.env.filter((_, at) => at !== index)})
													}
												/>
											</div>
										),
									)}
									<ActionButton
										small
										tone="quiet"
										icon={<IconPlus size={15} stroke={2} />}
										className={styles.addVar}
										onClick={() => set({env: [...form.env, {name: '', value: '', kept: false, removed: false}]})}
										data-flx="konvo-agents.accounts.editor.env.add"
									>
										<Trans comment="Agents accounts form: adds an environment variable row.">Add variable</Trans>
									</ActionButton>
								</div>
							</div>
						)}
						{error && (
							<Notice tone="danger" data-flx="konvo-agents.accounts.editor.error">
								{error}
							</Notice>
						)}
						<div className={styles.footer}>
							<ActionButton tone="secondary" onClick={onClose}>
								<Trans>Cancel</Trans>
							</ActionButton>
							<ActionButton
								tone="primary"
								type="submit"
								disabled={saving || !trimmed || badEnv || !machine.online}
								data-flx="konvo-agents.accounts.editor.save"
							>
								{existing ? (
									<Trans comment="Agents accounts form: saves the account's changes.">Save</Trans>
								) : (
									<Trans comment="Agents accounts form: adds the account, then signs it in.">Add and sign in</Trans>
								)}
							</ActionButton>
						</div>
					</form>
				</Modal.Content>
			</Modal.Root>
		);
	},
);

/** Opens the account editor: a new account (null) or an existing one. */
export function openAccountEditor(machine: AgentMachineView, connection: AgentConnection | null): void {
	const close = () => ModalCommands.popWithKey(EDIT_KEY);
	ModalCommands.pushWithKey(
		ModalCommands.modal(() => (
			<AccountEditor machineId={machine.id} provider={connection?.provider ?? null} onClose={close} />
		)),
		EDIT_KEY,
	);
}

// ── Sign in ─────────────────────────────────────────────────────────────

const TERMINAL_FAILED = msg({
	message: "Konvo couldn't open a terminal on {machine}. Run the command there instead.",
	comment: 'Agents sign-in: opening a terminal on the machine failed; the command to copy shows instead.',
});

/** The chat a sign-in terminal opens in: terminals belong to a chat on the machine. */
function hostThread(machine: AgentMachineView): string | null {
	if (!canUseTerminalSessions(machine)) return null;
	return (Agents.threadsOf(machine.id) ?? []).find((thread) => !thread.archivedAt)?.id ?? null;
}

const SignInTerminal = observer(
	({
		machine,
		threadId,
		command,
		onFailed,
	}: {
		machine: AgentMachineView;
		threadId: string;
		command: string;
		onFailed: () => void;
	}) => {
		const [terminalId, setTerminalId] = useState<string | null>(null);
		const [waits, setWaits] = useState(false);
		const phone = MobileLayout.enabled;
		const failed = useRef(onFailed);
		failed.current = onFailed;
		useEffect(() => {
			// Keeps the machine's terminal connection while the dialog shows the terminal.
			const release = AgentTerminalSessions.use(machine.id, threadId);
			let cancelled = false;
			runInTerminal({machineId: machine.id, threadId, phone, panel: false}, command).then(
				(started) => {
					if (cancelled) return;
					setTerminalId(started.id);
					setWaits(started.waits);
				},
				() => {
					if (!cancelled) failed.current();
				},
			);
			return () => {
				cancelled = true;
				release();
			};
		}, [machine.id, threadId, command, phone]);
		const terminal = terminalId
			? (AgentTerminalSessions.list(machine.id, threadId)?.find((entry) => entry.id === terminalId) ?? null)
			: null;
		return (
			<div className={clsx(panelStyles.view, styles.terminal)} data-flx="konvo-agents.accounts.sign-in.terminal">
				{terminal ? (
					<AgentTerminalScreen
						machineId={machine.id}
						threadId={threadId}
						terminal={terminal}
						phone={phone}
						disabled={false}
					/>
				) : (
					<p className={styles.terminalWait}>
						<Trans comment="Agents sign-in: while Konvo opens a terminal on the machine.">Opening a terminal…</Trans>
					</p>
				)}
				{waits && (
					<p className={styles.terminalHint}>
						<Trans comment="Agents sign-in: the command was typed but not run; the person presses Return.">
							Press Return in the terminal to run it.
						</Trans>
					</p>
				)}
			</div>
		);
	},
);

const SignInDialog = observer(
	({
		machineId,
		provider,
		added,
		onClose,
	}: {
		machineId: string;
		provider: string;
		added: boolean;
		onClose: () => void;
	}) => {
		const {i18n, t} = useLingui();
		const machine = Agents.machine(machineId);
		const connection = machineConnections(machine).find((entry) => entry.provider === provider) ?? null;
		const [threadId] = useState(() => (machine ? hostThread(machine) : null));
		const [noTerminal, setNoTerminal] = useState(false);
		const [checking, setChecking] = useState(false);
		const [problem, setProblem] = useState<string | null>(null);
		if (!machine || !connection) return null;
		const machineName = machine.name;
		const tool = machine.tools[0]?.name ?? 'T3 Code';
		const name = accountName(connection.providerName, connection.driver);
		const home = homeOf(connection);
		const command = connection.loginCommand ?? null;
		const ready = connection.status === 'ready';
		const terminal = !noTerminal && threadId && command && machine.online;
		const check = async () => {
			setChecking(true);
			setProblem(null);
			try {
				await Agents.checkAgentSetup(machine.id, {provider: driverOf(connection), instanceId: connection.provider});
			} catch (error) {
				setProblem(agentProblemMessage(i18n, error));
			} finally {
				setChecking(false);
			}
		};
		const close = () => {
			// The machine is asked once more on the way out, so Health and the list catch up.
			if (!ready && machine.online) void check();
			onClose();
		};
		return (
			<Modal.Root size="medium" centered onClose={close} data-flx="konvo-agents.accounts.sign-in">
				<Modal.Header
					title={t({
						message: `Sign in to ${name}`,
						comment: 'Agents sign-in: title; {name} is the account, e.g. Claude · Work 2.',
					})}
					onClose={close}
				/>
				<Modal.Content>
					<div className={styles.signIn}>
						{added && (
							<p className={styles.step}>
								<IconCircleCheck size={18} stroke={1.8} className={styles.stepDone} />
								<span>
									<Trans comment="Agents sign-in: first step, done. {tool} is e.g. T3 Code, {machineName} the computer, {home} the folder.">
										<b>
											Added to {tool} on {machineName}.
										</b>{' '}
										Sign-in folder {home}.
									</Trans>
								</span>
							</p>
						)}
						{ready ? (
							<Notice tone="neutral" icon={<StatusBadge state="ok" />} data-flx="konvo-agents.accounts.sign-in.done">
								{t({message: `${name} is signed in.`, comment: 'Agents sign-in: the account is signed in now.'})}
							</Notice>
						) : terminal ? (
							<>
								<p className={styles.step}>
									<span className={styles.stepNumber}>{added ? '2' : '1'}</span>
									<span>
										<Trans comment="Agents sign-in: what to do in the terminal. {machineName} is the computer.">
											<b>Sign in with the account for this subscription.</b> Konvo runs the sign-in on {machineName}:
											open the link it shows, then paste the code it asks for into the terminal.
										</Trans>
									</span>
								</p>
								<SignInTerminal
									machine={machine}
									threadId={threadId!}
									command={command!}
									onFailed={() => {
										setNoTerminal(true);
										setProblem(i18n._(TERMINAL_FAILED, {machine: machineName}));
									}}
								/>
							</>
						) : (
							<>
								<p className={styles.step}>
									<span className={styles.stepNumber}>{added ? '2' : '1'}</span>
									<span>
										<Trans comment="Agents sign-in: run the command by hand. {machineName} is the computer.">
											<b>Run this on {machineName}</b> and sign in with the account for this subscription. Then check
											again here.
										</Trans>
									</span>
								</p>
								{command ? (
									<CommandBlock lines={[command]} wrap data-flx="konvo-agents.accounts.sign-in.command" />
								) : (
									<p className={styles.quiet}>
										<Trans comment="Agents sign-in: the machine sent no sign-in command.">
											Sign in to this account in {tool} on {machineName}, then check again here.
										</Trans>
									</p>
								)}
							</>
						)}
						{problem && <Notice tone="danger">{problem}</Notice>}
						<div className={styles.footer}>
							{!ready && (
								<span className={styles.waiting}>
									{checking ? (
										<Trans comment="Agents sign-in: asking the machine whether the account is signed in.">
											Checking…
										</Trans>
									) : (
										<Trans comment="Agents sign-in: until the account is signed in.">Waiting for you to sign in…</Trans>
									)}
								</span>
							)}
							{ready ? (
								<ActionButton tone="primary" onClick={onClose} data-flx="konvo-agents.accounts.sign-in.close">
									<Trans comment="Agents sign-in: closes the dialog once signed in.">Done</Trans>
								</ActionButton>
							) : (
								<ActionButton
									tone="primary"
									icon={<IconRefresh size={15} stroke={1.8} />}
									disabled={checking || !machine.online}
									onClick={() => void check()}
									data-flx="konvo-agents.accounts.sign-in.check"
								>
									<Trans comment="Agents sign-in: asks the machine whether the account is signed in now.">
										Check again
									</Trans>
								</ActionButton>
							)}
						</div>
					</div>
				</Modal.Content>
			</Modal.Root>
		);
	},
);

/** Signs an account in: in a Konvo terminal on the machine when it can, else with the command to copy. */
export function openAccountSignIn(
	machine: AgentMachineView,
	connection: AgentConnection,
	options: {added?: boolean} = {},
): void {
	const close = () => ModalCommands.popWithKey(SIGN_IN_KEY);
	ModalCommands.pushWithKey(
		ModalCommands.modal(() => (
			<SignInDialog
				machineId={machine.id}
				provider={connection.provider}
				added={options.added === true}
				onClose={close}
			/>
		)),
		SIGN_IN_KEY,
	);
}
